import {Injectable, TemplateRef} from '@angular/core';
import * as XLSX from 'xlsx';


import {NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {saveAs} from "file-saver";

import {concatMap, from, Observable, Subject, takeWhile, toArray} from "rxjs";
import {Params} from "@angular/router";


import {tap} from "rxjs/operators";

import {IResponseApi} from '@core/models';
import {NotifyService} from '@shared/services/global/notify.service';
import {NgxModalCustomService} from '@shared/services/global/ngx-modal-custom.service';
import {NavService} from '@core/services/nav.service';
import {PAGING_LIMIT_HIGH} from '@shared/constants';
import {GOOGLE_SHEET_AUTH_CODE_PARAM, GOOGLE_SHEET_OPEN_MODAL_IMPORT} from '@shared/models/business';
import {castInt, isArray} from '@shared/utils';
import {LoggingService} from '@shared/services/api/logging';
import {ImportExcelNewModalComponent} from '@shared/components/excel/import-excel/modal';
import {
  IConfigImportDataGoogleSheet,
  ImportDataExcelSettingModalComponent,
  IOptionsImportDataExcelSetting
} from '@shared/components/excel/import-data';


/**
 * Cấu trúc dữ liệu API import cần trả giống nhau
 *
 * @Note:
 * - Chỉ khai báo cấu trúc dùng chung, các loại quá đặc biệt cần xử lý riêng không khai vào đây
 * */
type IRowsFlat = {
  type: 'success' | 'error' | 'warning',
  line: number,
  messages: any[]; // Là number với case rowSuccess[line] = [1]
}
export type IResImportFile = IResponseApi<{
  rowsFlat: IRowsFlat[],
  extendParam?: Record<string, any>,
  importId?: number,
  // Tình huống sau khi import đẩy file lên server cần nhận thêm dữ liệu trả về để FE tiếp tục xử lý thao tác với dữ liệu sau đó
  // VD: trang a/b/index đang dùng import file lấy data để thêm nhanh sản phẩm vào danh sách sản phẩm đã chọn
  resultData?: any
}>

type IResPrepaidImportFile = {
  extendParam?: Record<string, any>,
  importId: string, // ID log Elastic
  rowsData?: any[] // Import file từ GGS thì mới có dữ liệu
}

export type TypeLogImportExcel = 'all' | 'success' | 'error' | 'warning';

export type BaseOptionsImportExcel = {
  MSG_PROCESSING_ERROR: string;
  MSG_FILE_ERROR: string;

  // Config API
  url: string;
  limit: number,
  // params: Record<string, any>;
  // functionParams: Record<string, any>;

  // File
  fileUploadName: string; // Tên input filter của API
  fileUploadIsSave: boolean; // Có lưu file không không ? mặc định thì luôn lưu file

  // Options other
  titleModal?: string;
  useSubjectNextData?: boolean
}
type WithFunctionParams = {
  /**
   * Trường hợp muốn khởi tạo params từ component chính
   *
   * @param params - Các params luôn cần khi import excel
   * @param rows - Rows đọc từ file excel cần import
   * */
  functionParamsRows: (params: Params) => Params;
  params?: Params;
};

type WithParams = {
  params: Params;
  functionParamsRows?: (params: Params) => Params;
};

export type IOptionsImportExcel = BaseOptionsImportExcel & (WithFunctionParams | WithParams);

type IOptionsFile = {
  fileBinary: File | null; // File excel
  fileSheetName: string; // Sheet trong file cần đọc lấy data

  // Sate local
  stopImport: boolean;
  finishImport: boolean; // Biến đánh dấu hoàn thành việc import dữ liệu
  importId: string;
  paginator: {
    currentPage: number;
    totalPage: number;
    totalItem: number;
    currentRow: number;
    totalImported: number,
    totalRecord: number,
  };

  items: any[]; // Mảng dữ liệu đọc từ file excel
  workbook: Record<string, any>; // Lưu data read excel original
  header: Record<string, any>;

  // Log response
  rowsFlat: IRowsFlat[]
  totalSuccess: number;
  totalWarning: number;
  totalError: number;
}

/**
 * Config cần truyên khi gọi startImportExcel
 * */

type InputConfigImportExcel = {
  url: string, // Endpoint API
  fileUploadName?: string; // Tên input filter của API
  fileUploadIsSave?: boolean; // Có lưu file không không ? mặc định thì luôn lưu file

  // Cấu hình khác
  options?: {
    notOpenModal?: boolean, // Chặn không mở modal
    titleModal?: string, // Key translate
    // Xử lý lại giá trị khi là dạng mảng trước khi append vào formData
    // VD sau khi convert theo từng loại:
    // commonArray -> depotIds[0]: 313010, depotIds[1]: 213331,
    // jsonArray -> depotIds: [313010, 213331]
    // default -> depotIds: 313010, 213331
    convertArrayValue?: 'commonArray' | 'jsonArray',
    // Kích hoạt subject bắn dữ liệu khi response api import trả về có chưa data để FE nhận dữ liệu xử lý tiếp sau import
    useSubjectNextData?: boolean
  }
} & (WithFunctionParams | WithParams)


// Import data ------------------------------------------------
export type TFinalImportDataOpts = {
  pageSize?: number, // SL items / page
  setting: IConfigImportDataGoogleSheet,
  config: InputConfigImportExcel
}

export type IResImportDataGGS = IResponseApi<any[]>

/**
 * Service chỉ xử lý việc import dữ liệu qua excel
 *
 * @author admin
 * */
@Injectable({
  providedIn: 'root',
})
export class ImportExcelService {
  /**
   * Dữ liệu config global
   * - Khởi tạo theo component
   * - Reset khi thay đổi DN
   * */
  optionsImport: IOptionsImportExcel = this._genOptsImport();

  /**
   * Dữ liệu đi kèm với file
   * - Mỗi khi thay đổi file thì cẩn reset
   * - Tắt modal thông tin import thì reset
   * */
  optsFile: IOptionsFile = this._genOptsFile();
  refModal!: NgbModalRef
  // Thông báo nhận dữ liệu trả về sau khi import
  subjectNextData = new Subject<any>();
  private readonly importStateChangedSubject = new Subject<void>();
  readonly importStateChanged$ = this.importStateChangedSubject.asObservable();

  constructor(
    private notifyService: NotifyService,
    private modalService: NgxModalCustomService,
    private loggingService: LoggingService,
    private navService: NavService,
  ) {
  }


  /**
   * -----------------------------------------------------------------------------------------------------------------
   * Func init & reset -------------------------------------------------------------------------------------------------
   * -----------------------------------------------------------------------------------------------------------------
   * */
  private _genOptsFile(): IOptionsFile {
    return {
      fileBinary: null,// File excel
      fileSheetName: '',

      stopImport: false,
      finishImport: false,
      importId: '',

      // Sate local
      workbook: {},// Lưu data read excel original
      header: {},
      items: [], // Mảng dữ liệu đọc từ file excel
      paginator: {
        currentPage: 0,
        totalPage: 0,
        totalItem: 0,
        currentRow: 0,
        totalImported: 0,
        totalRecord: 0,
      },

      // Log response
      rowsFlat: [],
      totalSuccess: 0,
      totalWarning: 0,
      totalError: 0,
    }
  }

  // Thay đổi file thì reset lại thông tin opts của file
  private _resetOptsFile() {
    this.optsFile = this._genOptsFile();
    this.notifyImportStateChanged();
  }

  private _genOptsImport(): IOptionsImportExcel {
    return {
      MSG_PROCESSING_ERROR: 'Có lỗi trong quá trình thực hiện',
      MSG_FILE_ERROR: 'File không đúng format mẫu hoặc không có dữ liệu',
      url: '',
      params: {},
      fileUploadName: 'fileUpload',
      fileUploadIsSave: true,
      limit: this.optionsImport && this.optionsImport.limit ? this.optionsImport.limit : PAGING_LIMIT_HIGH,
    }
  }

  private _resetOptsImport() {
    this.optionsImport = this._genOptsImport();
  }


  /**
   * -----------------------------------------------------------------------------------------------------------------
   * Func change state -------------------------------------------------------------------------------------------------
   * -----------------------------------------------------------------------------------------------------------------
   * */
  stopImportExcel() {
    this.optsFile.stopImport = true;
    this.notifyImportStateChanged();
  }

  // Khi chọn file trong input view thì nạp dữ liệu vào optsFile
  // limit: Số items gửi lên trong 1 request
  onChangeFileBinary(file: File, fileSheetName: string, limit?: number): Promise<boolean> {
    this._resetOptsFile()
    this.optsFile.fileSheetName = fileSheetName;
    if (limit) {
      this.optionsImport.limit = limit;
    }

    this.optsFile.fileBinary = file
    return new Promise<boolean>(resolve => {
      const reader: FileReader = new FileReader()
      reader.onload = (e: any) => {
        try {
          /* Create workbook */
          const binaryStr: string = e.target.result;
          const workbook: XLSX.WorkBook = XLSX.read(binaryStr, {type: 'binary'})
          const rows: any = {}
          const sheetName = this.optsFile.fileSheetName

          if (typeof workbook != 'undefined' && typeof workbook.Sheets != 'undefined'
            && typeof workbook.Sheets[sheetName] != 'undefined') {
            for (const i in workbook.Sheets[sheetName]) {
              if (i.charAt(0) == '!') {
                continue;
              }
              const cell = workbook.Sheets[sheetName][i]
              const cellAddress = XLSX.utils.decode_cell(i)
              if (typeof rows[cellAddress.r] == 'undefined') {
                rows[cellAddress.r] = {}
              }
              rows[cellAddress.r][cellAddress.c] = cell.v

            }
          } else {
            this.errorHandlerImport([this.optionsImport.MSG_FILE_ERROR])
            resolve(false)
            return
          }
          this.optsFile.header = rows[0]
          delete rows[0]
          this._readAndParseItems(rows)
          resolve(this.optsFile.paginator.totalItem > 0)
        } catch {
          this.errorHandlerImport([this.optionsImport.MSG_FILE_ERROR])
          resolve(false)
        }
      }
      reader.onerror = () => {
        this.errorHandlerImport([this.optionsImport.MSG_FILE_ERROR])
        resolve(false)
      }
      reader.readAsBinaryString(file)
    })
  }

  /**
   * Đọc dữ liệu từ file excel nạp vào state để xử lý
   * */
  private _readAndParseItems(rows: any) {
    const items: any = {}
    let index = 2
    if (rows) {
      for (const i in rows) {
        if (rows[i]) {
          const item: any = {}
          let isValid = false
          for (const col in rows[i]) {
            if (col in this.optsFile.header) {
              item[this.optsFile.header[col]] = rows[i][col]
            }
            if (rows[i][col]) {
              isValid = true
            }
          }
          if (isValid) {
            items[index] = item
          }
        }
        index++
      }
    }
    if (index <= 2) {
      return this.errorHandlerImport([this.optionsImport.MSG_FILE_ERROR])
    }
    this.optsFile.items = items
    this.handlePaginatorImport()
  }

  /**
   * - Case 1: Được kích hoạt từ this._readAndParseItems()
   * - Case 2: Được kích hoạt thủ công từ component của màn hình tự xử lý riêng
   * */
  handlePaginatorImport() {
    // Reset
    this.optsFile.paginator.currentPage = 1
    this.optsFile.paginator.currentRow = 2
    this.optsFile.paginator.totalItem = 0
    this.optsFile.paginator.totalPage = 0

    // Handle
    const totalItem = Object.keys(this.optsFile.items).length
    if (totalItem) {
      this.optsFile.paginator.totalItem = totalItem
      const x = this.optsFile.paginator.totalItem / this.optionsImport.limit;
      this.optsFile.paginator.totalPage = Math.ceil(x)
    }
  }

  /**
   * -----------------------------------------------------------------------------------------------------------------
   * Func handle -------------------------------------------------------------------------------------------------
   * -----------------------------------------------------------------------------------------------------------------
   * - Step 1 startImport():
   * + Kiểm tra dữ liệu
   * + Call API lần đầu để lưu file excel trên server
   *
   * - Step 2 processingImport():
   * + Đọc dữ liệu từ rows excel lấy được phân trang => call API
   * ++ Cắt dữ liệu theo optionsImport.limit
   * -
   * */

  /**
   * Bắt đầu việc call API để import data
   * @param config - Đọc thêm mô tả trong interface đầu file IConfigImportExcel
   */
  startImportExcel(config: InputConfigImportExcel) {
    if (!config.url) {
      return this.errorHandlerImport('common.invalidUrl')
    }

    // Reset lại toàn bộ data
    this._resetOptsImport();

    // Khởi tạo options theo config nhận được
    this.optionsImport.url = config.url;

    if (config.fileUploadName) {
      this.optionsImport.fileUploadName = config.fileUploadName;
    }
    if (typeof config.fileUploadIsSave == "boolean") {
      this.optionsImport.fileUploadIsSave = config.fileUploadIsSave;
    }
    if (config.params) {
      this.optionsImport.params = config.params
    }
    if (config.functionParamsRows && typeof config.functionParamsRows == "function") {
      this.optionsImport.functionParamsRows = config.functionParamsRows
    }
    if (config?.options?.titleModal) {
      this.optionsImport.titleModal = config?.options?.titleModal;
    }
    if (!config.options || !config?.options.notOpenModal) {
      this.openModalImport()
    }
    if (config?.options?.useSubjectNextData) {
      this.optionsImport.useSubjectNextData = config?.options?.useSubjectNextData;
    }

    // Post trước lên 1 lần để validate các dữ liệu
    const formData = new FormData()
    formData.append('isPrepair', '1')
    formData.append('totalRows', this.optsFile.paginator.totalItem.toString())

    let paramsFormData = {};
    if (this.optionsImport.functionParamsRows) {
      paramsFormData = this.optionsImport.functionParamsRows({})
    } else if (this.optionsImport.params) {
      paramsFormData = this.optionsImport.params;
    }

    Object.entries(paramsFormData).forEach(([paramName, value]) => {
      if (Array.isArray(value) && config?.options?.convertArrayValue) {
        switch (config.options.convertArrayValue) {
          case "commonArray":
            value.forEach((v, index) => {
              formData.append(`${paramName}[${index}]`, v as string);
            })
            break;
          case "jsonArray":
            formData.append(paramName, JSON.stringify(value));
            break
        }
      } else {
        formData.append(paramName, value as string);
      }
    });

    // Kiểm tra có có lưu file excel không
    if (this.optionsImport.fileUploadIsSave && this.optsFile.fileBinary) {
      formData.append(this.optionsImport.fileUploadName, this.optsFile.fileBinary)
    }
    this.postPrepaidImportExcel(formData).subscribe({
      next: (rs) => {
        if (!rs.code) {
          this.notifyService.error(rs.messages ?? this.optionsImport.MSG_PROCESSING_ERROR)
          return;
        }

        if (rs.data.importId) {
          this.optsFile.importId = rs.data.importId
        }
        if (rs.data?.extendParam) {
          this.optionsImport.params = {...this.optionsImport.params, ...rs.data.extendParam}
        }
        this._processingImport()
      },
      error: (e) => this.errorHandlerImport(e)
    })
  }


  /**
   * Chạy tiến trình import hiển thị ra modal
   * @param options
   */
  private _processingImport(options: any | null = null) {
    if (this.optsFile.stopImport) {
      return
    }

    if (this.optsFile.paginator.currentPage > this.optsFile.paginator.totalPage
      || this.optsFile.paginator.currentRow > this.optsFile.paginator.totalItem + 1) {
      return this.finishModalImport()
    }

    if (!this.optionsImport.url) {
      return this.errorHandlerImport('common.invalidUrl')
    }

    const limit = this.optionsImport.limit;
    let startItemIndex = this.optsFile.paginator.currentRow
    let endItemIndex = limit == 1 ? startItemIndex : startItemIndex + limit - 1
    if (endItemIndex > this.optsFile.paginator.totalItem + 1) {
      endItemIndex = this.optsFile.paginator.totalItem + 1
    }
    const items: any = {};
    for (startItemIndex; startItemIndex <= endItemIndex; startItemIndex++) {
      if (startItemIndex in this.optsFile.items) {
        items[startItemIndex] = this.optsFile.items[startItemIndex];
      }
    }


    let params: Params = {
      'rows': JSON.stringify(items),
      'page': this.optsFile.paginator.currentPage,
      'totalPage': this.optsFile.paginator.totalPage,
      'totalRows': this.optsFile.paginator.totalItem,
      'icpp': this.optionsImport.limit,
      'start': this.optsFile.paginator.currentRow,
      'importId': this.optsFile.importId,
      ...this.optionsImport.params
    }
    if (this.optionsImport.functionParamsRows) {
      // Tự khởi tạo params
      // VD rows excel sẽ đặt trong params.data.rows
      params = this.optionsImport.functionParamsRows(params)
    }
    this.processingImportExcel(params).subscribe({
      next: (rs) => {
        if (!rs.code) {
          this.stopImportExcel();
          this.notifyService.error(rs.messages?.[0] ?? this.optionsImport.MSG_PROCESSING_ERROR)
          return;
        }
        if (this.optionsImport.useSubjectNextData && rs.data.resultData) {
          this.subjectNextData.next(rs.data.resultData)
        }

        this.optsFile.paginator.currentRow = startItemIndex
        this.optsFile.paginator.currentPage++
        // Ghi ra log
        this.writeLogsImport(rs)
        this.changeModalState()
        if (rs.data && rs.data?.extendParam) {
          this.optionsImport.params = {...this.optionsImport.params, ...rs.data.extendParam}
        }

        if (options && options?.delay && parseInt(options?.delay)) {
          setTimeout(() => {
            this._processingImport(options)
          }, options.delay)
        } else {
          this._processingImport()
        }
      },
      error: (e: Error) => {
        this.errorHandlerImport(e.message)
      }
    })
  }

  /**
   * - Case 1: Nhận lỗi từ API trả về
   * - Case 2: Được set lỗi chủ động từ component của màn hình
   * */
  errorHandlerImport(messages: string | string[]) {
    this.notifyService.error(messages)
    this.stopImportExcel();
    this._closeModalImport()
    return;
  }


  /**
   * Hiển thị trạng thái thanh tiến trình đang chạy thống kê các trạng thái import
   *
   * - Case 1: Được kích hoạt từ this._processingImport()
   * - Case 2: Được kích hoạt thủ công từ component của màn hình tự xử lý riêng
   *
   * - Tên cũ "changeModalStage'
   */
  changeModalState() {
    // Trừ đi 1 dong header, 1 dòng lấy lúc đầu
    this.optsFile.paginator.totalImported = this.optsFile.paginator.currentRow - 2;
    this.optsFile.paginator.totalRecord = this.optsFile.paginator.totalItem;
    this.notifyImportStateChanged();
  }

  /**
   * Lần đầu gọi server để kiểm tra dữ liệu và lưu file excel vào server
   *
   * - Case 1: Được kích hoạt từ this.startImport()
   * - Case 2: Được kích hoạt thủ công từ component của màn hình tự xử lý riêng
   */
  postPrepaidImportExcel(formParam: FormData, url?: string): Observable<IResponseApi<IResPrepaidImportFile>> {
    return this.loggingService.postFormDataRequest(url ? url : this.optionsImport.url, formParam)
  }

  /**
   * Gửi dữ liệu theo phân trang để lưu vào server
   *
   * - Case 1: Được kích hoạt từ this._processingImport()
   * - Case 2: Được kích hoạt thủ công từ component của màn hình tự xử lý riêng
   */
  processingImportExcel = (params = {}) => {
    return this.loggingService.postRequest<IResImportFile>(this.optionsImport.url, params)
  }

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * Func handle logs ------------------------------------------------------------------------------------------------
   * ------------------------------------------------------------------------------------------------------------------
   * - exportSaveFileLogs: export file log .txt
   * */
  /**
   * Xử lý logs nhận về từ API
   */
  writeLogsImport(res: IResImportFile) {
    const rowsFlat = (res.data?.rowsFlat ?? []).map(log => ({
      ...log,
      messages: this.normalizeLogMessages(log.messages),
    }));
    this.optsFile.rowsFlat = [...this.optsFile.rowsFlat, ...rowsFlat]
    this.optsFile.totalSuccess = 0;
    this.optsFile.totalError = 0;
    this.optsFile.totalWarning = 0;
    for (const log of this.optsFile.rowsFlat) {
      switch (log.type) {
        case 'success':
          this.optsFile.totalSuccess++;
          break;

        case 'error':
          this.optsFile.totalError++;
          break;

        case 'warning':
          this.optsFile.totalWarning++;
          break;
      }
    }
    this.notifyImportStateChanged();
  }

  private normalizeLogMessages(messages: any): any[] {
    if (!Array.isArray(messages)) {
      return [messages];
    }

    return messages.reduce<any[]>((result, message) => {
      return [...result, ...(Array.isArray(message) ? this.normalizeLogMessages(message) : [message])];
    }, []);
  }

  private notifyImportStateChanged() {
    this.importStateChangedSubject.next();
  }

  exportFileLogsImport() {
    let logText = '';
    const date = new Date();
    const month = date.getMonth() + 1;
    logText += "Import time: "
      + date.getFullYear() + "-"
      + month + "-"
      + date.getDate() + " "
      + date.getHours() + ":"
      + date.getMinutes() + ":"
      + date.getSeconds() + "\n\n";

    let rowsSuccess = '';
    let rowsErrors = '';
    let rowsWarning = '';

    for (const log of this.optsFile.rowsFlat) {
      switch (log.type) {
        case 'success':
          rowsSuccess += "Dòng " + log.line + "\n";
          break;

        case 'error':
          rowsErrors += "Dòng " + log.line + "\n";
          for (const mess of log.messages) {
            rowsErrors += " - " + mess + "\n";
          }
          rowsErrors += "\n";
          break;

        case 'warning':
          rowsWarning += "Dòng " + log.line + "\n";
          for (const mess of log.messages) {
            rowsWarning += " - " + mess + "\n";
          }
          rowsWarning += "\n";
          break;
      }
    }

    if (rowsSuccess) {
      logText += "===================Thành công==============\n";
      logText += rowsSuccess;
      logText += "\n\n";
    }

    if (rowsErrors) {
      logText += "===================Lỗi==============\n";
      logText += rowsErrors;
      logText += "\n\n";
    }

    if (rowsWarning) {
      logText += "===================Cảnh báo==============\n";
      logText += rowsWarning;
      logText += "\n\n";
    }


    let name = 'log_import_' + date.getFullYear() + "_" + month + "_" + date.getDate();
    name += "_" + date.getHours() + "_" + date.getMinutes() + "_" + date.getSeconds() + ".txt";

    saveAs(new Blob([logText], {type: "text/plain;charset=utf-8"}), name);
  }

  /**
   * -----------------------------------------------------------------------------------------------------------------
   * Func handle modal ----------------------------------------------------------------------------------------------
   * -----------------------------------------------------------------------------------------------------------------
   * - exportSaveFileLogs: export file log .txt
   * */
  /**
   * - Case 1: Được kích hoạt từ this.startImport()
   * - Case 2: Được kích hoạt thủ công từ component của màn hình tự xử lý riêng
   * */
  openModalImport() {
    this.refModal = this.modalService.openDefault(ImportExcelNewModalComponent, {size: 'lg'})
    this.refModal.componentInstance.service = this;
    this.refModal.dismissed.subscribe({
      next: () => {
        this.stopImportExcel();
        //@Todo: Check lại resetOptsFile sau khi tắt modal Import dẫn đến optsFile.finishImport = false
        // this._resetOptsFile();
      }
    })

  }

  /**
   * - Case 1: Được kích hoạt từ this._processingImport()
   * - Case 2: Được kích hoạt thủ công từ component của màn hình tự xử lý riêng
   * */
  finishModalImport() {
    this.stopImportExcel();
    this.optsFile.finishImport = true
    this.notifyImportStateChanged();
  }

  private _closeModalImport() {
    if (this.refModal) {
      this.refModal.close()
    }
  }


  /**
   * -----------------------------------------------------------------------------------------------------------------
   * -----------------------------------------------------------------------------------------------------------------
   * Function handle Google Sheets ----------------------------------------------------------------------------------
   * -----------------------------------------------------------------------------------------------------------------
   * -----------------------------------------------------------------------------------------------------------------
   *
   * Các phương thức xử lý toàn bộ thao tác cho modal import "File excel" và "Google Sheets"
   *
   * */
  settingImportData!: IConfigImportDataGoogleSheet;
  contentTmpImportData!: TemplateRef<any>
  refImportModal!: NgbModalRef

  checkOpenImportDataSetting() {
    const authCode = !!this.navService.getParam(GOOGLE_SHEET_AUTH_CODE_PARAM);
    const isOpen = !!this.navService.getParam(GOOGLE_SHEET_OPEN_MODAL_IMPORT);
    return authCode || isOpen;
  }

  // Init -----------------------------------------------------------------------------------------------------------------
  onInitImportData() {
    this.settingImportData = {
      tabRef: 'excel',
      accessTokenId: undefined,
      businessId: 0,
      spreadId: '',
      spreadSheetId: 0,
      spreadSheetTitle: "",
      spreadUrl: "",
      isRunning: false,
      isFinal: false,
      isImportSubmit: false,
      headersFinal: {},
      progressPaginator: {
        page: 0,
        totalPages: 0,
        totalItems: 0,
        totalHandleItems: 0, // Tổng số items đã xử lý xong
        isError: false,
      },
      urlApi: '',
      params: undefined
    }
  }

  /**
   * Mở modal export data
   * - Chú tất cả các key label hay hiển thị tên cần truyền key translate
   * @Docs:
   * - https://docs.google.com/document/d/1tGwUmV8q-NtGCP4VLkiTuIdaJRiOh_upHgnU7YQFxCg/edit#heading=h.rv1ly6gyf1qw
   * */
  openImportDataModal(opts: IOptionsImportDataExcelSetting, destroy: Subject<any>) {
    if (!opts || !opts.businessId) {
      return;
    }
    this._resetOptsFile();

    const modalRef = this.modalService.openDefault(ImportDataExcelSettingModalComponent, {size: 'lg'});
    const refInstance = modalRef.componentInstance as ImportDataExcelSettingModalComponent;
    refInstance.options = opts;

    modalRef.dismissed.subscribe({
      next: () => {
        this.navService.removeParams(['isOpenExportGG', 'tabRef', 'tokenError'])
        this.stopImportExcel()
      }
    })

    // @TODO NVN: Sau nếu không cần tắt modal sau khi import xong thì sẽ xóa hẳn đoạn code này
    // this.exportSuccess.pipe(takeUntil(destroy)).subscribe({
    // next: success => {
    // if (success) {
    // modalRef.close();
    // }
    // }
    // })

    this.refImportModal = modalRef;
    return refInstance;
  }

  onHandleStartImportData(opts: TFinalImportDataOpts) {
    const {config} = opts;
    // this.settingImportData = opts.setting;
    const setting = this.settingImportData;
    setting.urlApi = config.url;
    setting.pageSize = opts.pageSize;
    setting.params = config.params;

    if (setting.tabRef == 'excel') {
      // if (this.refImportModal){
      // this.refImportModal.dismiss();
      // }

      return this.startImportExcel(config);
    }

    // Step 1: Pre handle
    const formData = new FormData()
    formData.append('useSourceDataImport', '1')
    formData.append('totalRows', setting.progressPaginator.totalItems.toString())
    formData.append('spreadUrl', setting.spreadUrl)
    formData.append('spreadSheetId', setting.spreadSheetId.toString())
    formData.append('formDataClient', setting.params ? JSON.stringify(setting.params) : '')
    this.postPrepaidImportExcel(formData, `${setting.urlApi}?prepaidImport=1`).subscribe({
      next: (rs) => {
        if (!rs.code) {
          this.notifyService.error(rs.messages ?? this.optionsImport.MSG_PROCESSING_ERROR)
          this._handleErrorImportData(rs.messages);
        } else {
          const items = rs.data.rowsData ?? [];
          if (!items || !isArray(items)) {
            this._handleErrorImportData('common.messages.invalidData');
          } else {

            setting.params = {
              ...setting.params,
              ...rs.data.extendParam,
              importId: rs.data.importId
            }

            // Step 2: Fetch data page 1
            setting.isRunning = true;
            setting.progressPaginator.page = castInt(setting.progressPaginator.page)
            this._queuePages();
          }
        }
      }
    })
  }

  // Step 2: Fetch data page 1
  private _queuePages() {
    const setting = this.settingImportData;
    // let pageSize = setting.pageSize;
    // if (!pageSize || pageSize > 200) {
    // pageSize = 200;
    // }
    setting.progressPaginator.page++;

    this.loggingService.postRequest<IResImportDataGGS>(
      `${setting.urlApi}?page=${setting.progressPaginator.page}`,
      {
        ...setting.params,
        accessTokenId: setting.accessTokenId,
        spreadId: setting.spreadId,
        spreadSheetTitle: setting.spreadSheetTitle,
        page: setting.progressPaginator.page,
        pageSize: 200,
        headersFinal: JSON.stringify(setting.headersFinal),
        useSourceDataImport: 1
      }).subscribe({
      next: (rsFirst: any) => {
        this.settingImportData.progressPaginator.isError = !rsFirst.code;

        if (!rsFirst.code) {
          this._handleErrorImportData(rsFirst.messages);
        } else {
          // Step 3: Save row queue
          this._queuePageItems(rsFirst.data)
        }
      }
    })
  }

  // Step 3: Save row queue
  private _queuePageItems(items: any[]) {
    const setting = this.settingImportData;
    from(Object.entries(items)).pipe(
      concatMap(([rowKey, item]) => {
        return this.loggingService
          .postRequest<IResImportFile>(setting.urlApi, {
            ...setting.params,
            useHeaderOriginal: 1,
            rows: JSON.stringify({[rowKey]: item})
          })
          .pipe(
            tap({
              next: (rsItem: any) => {
                this.settingImportData.progressPaginator.totalHandleItems++;
                if (!rsItem.code) {
                  this._handleErrorImportData(rsItem.messages)
                } else {
                  this.writeLogsImport(rsItem)

                  if (rsItem.data && rsItem.data?.extendParam) {
                    setting.params = {
                      ...setting.params,
                      ...rsItem.data.extendParam
                    }
                  }
                }
              }
            })
          )
      }),
      toArray(),
      takeWhile(() => !this.optsFile.stopImport)
    ).subscribe({
      next: () => {
        if (setting.progressPaginator.page >= setting.progressPaginator.totalPages) {
          this.optsFile.finishImport = true
          setting.isFinal = true;
          this.notifyImportStateChanged();
          return;
        }
        this._queuePages()
      }
    });
  }


  private _handleErrorImportData(messages: any[] | string) {
    this.stopImportExcel();
    this.settingImportData.progressPaginator.isError = true;
    this.notifyService.error(messages ?? this.optionsImport.MSG_PROCESSING_ERROR)
    return false;
  }
}
